import styled from '@emotion/styled'
import { faCalendar, faNewspaper } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getAllNews } from '../../../../api/news.api'
import { useLangSelected } from '../../../../hooks/useLangSelected'
import DashboardCard from '../components/DashboardCard'
import EmptyState from '../components/EmptyState'
import SkeletonLoader from '../components/SkeletonLoader'
import StatCard from '../components/StatCard'
import { NewsStatsData } from '../types/widget-data.type'

const NewsList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`

const NewsItem = styled.li`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    border-bottom: 1px solid #e5e7eb;
    font-size: 1.4rem;
    color: #4b5563;

    &:last-child {
        border-bottom: none;
    }
`

const NewsDate = styled.span`
    font-size: 1.2rem;
    color: #9ca3af;
    margin-left: auto;
`

const NewsStatsWidget = () => {
    const { t } = useTranslation()
    const lang = useLangSelected()

    const { data: news, isLoading } = useQuery({
        queryKey: ['news', lang],
        queryFn: () => getAllNews(lang),
    })

    if (isLoading) {
        return <SkeletonLoader showHeader itemCount={3} itemHeight="50px" />
    }

    const newsArray = Array.isArray(news) ? news : []

    const data: NewsStatsData = {
        totalNews: newsArray.length || 0,
        recentNews:
            newsArray.slice(0, 3).map((n: any) => ({
                id: n.id,
                title: n.title,
                createdAt: n.createdAt,
            })) || [],
    }

    const formatDate = (dateString: string) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
    }

    return (
        <DashboardCard
            title={t('adminDashboard.widgets.newsStats.title')}
            icon={<FontAwesomeIcon icon={faNewspaper} />}
        >
            <StatCard
                label={t('adminDashboard.widgets.newsStats.total')}
                value={data.totalNews}
                icon={<FontAwesomeIcon icon={faNewspaper} />}
                color="blue"
            />
            {data.recentNews.length > 0 ? (
                <div style={{ marginTop: '16px' }}>
                    <p style={{ fontSize: '1.4rem', fontWeight: '500', color: '#374151', marginBottom: '12px' }}>
                        {t('adminDashboard.widgets.newsStats.recent')}
                    </p>
                    <NewsList>
                        {data.recentNews.map((item) => (
                            <NewsItem key={item.id}>
                                <FontAwesomeIcon icon={faNewspaper} style={{ fontSize: '1.2rem', color: '#9ca3af' }} />
                                <span
                                    style={{
                                        flex: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {item.title}
                                </span>
                                <NewsDate>
                                    <FontAwesomeIcon icon={faCalendar} style={{ marginRight: '4px' }} />
                                    {formatDate(item.createdAt)}
                                </NewsDate>
                            </NewsItem>
                        ))}
                    </NewsList>
                </div>
            ) : (
                <EmptyState title="Aucun article" description="Il n'y a aucun article publié pour le moment." />
            )}
        </DashboardCard>
    )
}

export default NewsStatsWidget
